import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import { Button, Card, Input, OTPInput } from "@nutrayan/ui";
import { emailSchema } from "@nutrayan/utils";
import type { AdminLoginResult, TokenPair } from "@nutrayan/api-client";
import { useAuth } from "@/lib/auth";
import { adminLogin, adminVerifyMfa, adminEnrollMfa, adminSkipMfa } from "@/lib/admin-auth";
import { StatusScreen, StatusIcons } from "@/components/StatusScreen";
import { PasswordField } from "@/components/PasswordField";
import { CredentialFlow, type CredentialVariant } from "./CredentialFlow";

const identifierSchema = z.object({ email: emailSchema });

/** Best-effort detection of an account-locked 401 (backend failureReason=account_locked). */
function isLockedError(err: unknown): boolean {
  const e = err as {
    problem?: { detail?: string; title?: string; code?: string };
    message?: string;
  };
  const haystack = [e?.problem?.detail, e?.problem?.title, e?.problem?.code, e?.message]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes("lock");
}

type Screen = "identifier" | "password" | "mfa";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [screen, setScreen] = useState<Screen>("identifier");
  const [credential, setCredential] = useState<CredentialVariant | null>(null);
  const [locked, setLocked] = useState(false);

  const [password, setPassword] = useState("");
  const [mfaToken, setMfaTokenLocal] = useState<string | null>(null);
  const [mfaMode, setMfaMode] = useState<"verify" | "enroll">("verify");
  const [mfaOptional, setMfaOptional] = useState(false);
  const [otpAuthUri, setOtpAuthUri] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const form = useForm<{ email: string }>({
    resolver: zodResolver(identifierSchema),
    defaultValues: { email: "" },
  });
  const email = form.watch("email");

  const goToDashboard = () => {
    // Return to the originally-requested page if RequireAuth captured one.
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== "/login" ? from : "/dashboard", { replace: true });
  };

  const finishLogin = (tokens: TokenPair | undefined) => {
    if (!tokens?.accessToken) {
      toast.error("Sign-in failed — no token issued");
      return;
    }
    auth.setToken(tokens.accessToken, email.trim(), tokens.refreshToken);
    toast.success("Welcome back");
    goToDashboard();
  };

  const resetToIdentifier = () => {
    setScreen("identifier");
    setPassword("");
    setMfaTokenLocal(null);
    setMfaCode("");
    setMfaOptional(false);
    setOtpAuthUri(null);
  };

  const loginMutation = useMutation({
    mutationFn: () => adminLogin(email.trim(), password),
    onSuccess: (res: AdminLoginResult) => {
      switch (res.state) {
        case "AUTHENTICATED":
          finishLogin(res.tokens);
          break;
        case "MFA_CHALLENGE":
          if (res.mfaToken) {
            setMfaTokenLocal(res.mfaToken);
            setMfaMode("verify");
            setScreen("mfa");
          } else {
            toast.error("MFA challenge missing token — try again");
          }
          break;
        case "ENROLLMENT_REQUIRED":
          if (res.mfaToken) {
            setMfaTokenLocal(res.mfaToken);
            setMfaMode("enroll");
            setMfaOptional(res.mfaOptional ?? false);
            setOtpAuthUri(res.otpAuthUri ?? null);
            setScreen("mfa");
          } else {
            toast.error("MFA enrollment missing token — try again");
          }
          break;
        case "PASSWORD_SETUP_REQUIRED":
          // No password on file — route to OTP setup; never show "invalid password".
          setCredential("setup");
          break;
        default:
          toast.error("Unexpected login response");
      }
    },
    onError: (err: Error) => {
      // Locked → dedicated unlock path. Everything else (wrong password, unknown,
      // suspended) returns the SAME generic message by design — no enumeration.
      if (isLockedError(err)) {
        setLocked(true);
        return;
      }
      toast.error("We couldn't sign you in. Check your details and try again.");
    },
  });

  const mfaMutation = useMutation({
    mutationFn: (totpCode: string) =>
      mfaMode === "enroll"
        ? adminEnrollMfa(mfaToken ?? "", totpCode)
        : adminVerifyMfa(mfaToken ?? "", totpCode),
    onSuccess: (tokens) => finishLogin(tokens),
    onError: (err: Error) => toast.error(err.message || "Invalid code"),
  });

  const skipMfaMutation = useMutation({
    mutationFn: () => adminSkipMfa(mfaToken ?? ""),
    onSuccess: (tokens) => finishLogin(tokens),
    onError: (err: Error) => toast.error(err.message || "Couldn't skip MFA — enrol to continue"),
  });

  // --- Terminal / sub-flow screens ---------------------------------------

  if (locked) {
    return (
      <StatusScreen
        icon={StatusIcons.locked}
        tone="danger"
        title="Account locked"
        description="Too many failed attempts. For your security this account is temporarily locked. Unlock it with a one-time code sent to your email."
        primaryAction={{
          label: "Unlock account",
          onClick: () => {
            setLocked(false);
            setCredential("unlock");
          },
        }}
        secondaryAction={{ label: "Back to sign in", onClick: () => setLocked(false) }}
      />
    );
  }

  if (credential) {
    return (
      <div className="bg-sand flex min-h-screen items-center justify-center p-4">
        <CredentialFlow
          variant={credential}
          initialEmail={email.trim()}
          onBack={() => setCredential(null)}
          onDone={(confirmedEmail) => {
            setCredential(null);
            resetToIdentifier();
            form.setValue("email", confirmedEmail);
            setScreen("password");
          }}
        />
      </div>
    );
  }

  // --- Login (progressive identifier → password → MFA) -------------------

  return (
    <div className="bg-sand flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center gap-2">
          <span className="bg-saffron/15 text-saffron font-display flex h-9 w-9 items-center justify-center rounded-md text-lg">
            N
          </span>
          <div>
            <h1 className="font-display text-display-sm text-charcoal leading-none">
              Nutrayan Admin
            </h1>
            <p className="text-caption text-muted">Secure administrator access</p>
          </div>
        </div>

        {screen === "identifier" ? (
          <form
            className="mt-6 space-y-4"
            onSubmit={form.handleSubmit(() => setScreen("password"))}
          >
            <div>
              <label className="text-body-sm text-ink" htmlFor="email">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                autoFocus
                {...form.register("email")}
                invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email ? (
                <p className="text-caption text-terracotta mt-1">
                  {form.formState.errors.email.message}
                </p>
              ) : null}
            </div>
            <Button size="lg" fullWidth type="submit">
              Continue
            </Button>
          </form>
        ) : null}

        {screen === "password" ? (
          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (password && !loginMutation.isPending) loginMutation.mutate();
            }}
          >
            <div className="bg-sand flex items-center justify-between rounded-md px-3 py-2">
              <span className="text-body-sm text-charcoal truncate">{email}</span>
              <button
                type="button"
                onClick={resetToIdentifier}
                className="text-caption text-forest shrink-0 underline"
              >
                Change
              </button>
            </div>
            <div>
              <label className="text-body-sm text-ink" htmlFor="password">
                Password
              </label>
              <PasswordField
                id="password"
                autoComplete="current-password"
                placeholder="Your password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button
              size="lg"
              fullWidth
              type="submit"
              disabled={loginMutation.isPending || !password}
            >
              {loginMutation.isPending ? "Signing in…" : "Sign in"}
            </Button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setCredential("forgot")}
                className="text-caption text-forest underline"
              >
                Forgot password?
              </button>
              <button
                type="button"
                onClick={() => setCredential("unlock")}
                className="text-caption text-muted hover:text-ink underline"
              >
                Unlock account
              </button>
            </div>
          </form>
        ) : null}

        {screen === "mfa" ? (
          <div className="mt-6 space-y-4">
            {mfaMode === "enroll" ? (
              <div className="bg-sand rounded p-3">
                <p className="text-body-sm text-ink">
                  First login: add this account to your authenticator app, then enter the first
                  6-digit code.
                </p>
                {otpAuthUri ? (
                  <p className="text-caption text-muted mt-2 break-all font-mono">{otpAuthUri}</p>
                ) : null}
              </div>
            ) : (
              <p className="text-body-sm text-ink">
                Enter the 6-digit code from your authenticator.
              </p>
            )}
            <OTPInput
              value={mfaCode}
              onChange={setMfaCode}
              onComplete={(code) => mfaMutation.mutate(code)}
              invalid={mfaMutation.isError}
            />
            <Button
              size="lg"
              fullWidth
              onClick={() => mfaMutation.mutate(mfaCode)}
              disabled={mfaMutation.isPending || mfaCode.length < 6}
            >
              {mfaMutation.isPending
                ? "Verifying…"
                : mfaMode === "enroll"
                  ? "Enroll & sign in"
                  : "Verify"}
            </Button>
            {mfaMode === "enroll" && mfaOptional ? (
              <Button
                variant="ghost"
                size="md"
                fullWidth
                onClick={() => skipMfaMutation.mutate()}
                disabled={skipMfaMutation.isPending}
              >
                {skipMfaMutation.isPending ? "Skipping…" : "Skip for now"}
              </Button>
            ) : null}
            <button
              type="button"
              onClick={() => setScreen("password")}
              className="text-caption text-forest mx-auto flex items-center gap-1 underline"
            >
              <ArrowLeft size={12} aria-hidden /> Back
            </button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
